import React, { useCallback, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '@/services/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Loader2, Monitor, Smartphone, Tablet, X,
  ChevronDown, ChevronUp, Shield, AlertTriangle, Info, Clock, Activity,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { TwoFactorSetup } from '../TwoFactorSetup';
import { useAuthStore } from '@/stores/auth/auth-store';
import * as errorUtils from '@/utils/errors';

// ─── Security config interfaces ─────────────────────────────

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

interface Session {
  sessionId: string;
  device: {
    type: string;
    browser: string;
    os: string;
  };
  ipAddress: string;
  createdAt: string;
  lastActivityAt: string;
  isActive: boolean;
}

interface TwoFactorStatus {
  enabled: boolean;
  hasBackupCodes: boolean;
  backupCodesGeneratedAt?: string | Date | null;
}

// ─── Activity / audit interfaces ─────────────────────────────

interface SecurityAlertAPI {
  id?: string;
  _id?: string;
  eventType: string;
  timestamp: string;
  severity: 'info' | 'warning' | 'critical';
  metadata: Record<string, unknown>;
}

interface SecurityAlert {
  id: string;
  type: 'warning' | 'critical' | 'info';
  title: string;
  description: string;
  timestamp: Date;
}

interface LoginHistoryAPI {
  id?: string;
  _id?: string;
  timestamp: string;
  device: {
    type: string;
    browser: string;
    os: string;
  };
  ipAddress: string;
  status: 'success' | 'failed' | 'blocked';
  method: string;
}

interface LoginHistory {
  id: string;
  date: Date;
  device: string;
  location: string;
  ipAddress: string;
  status: 'success' | 'failed' | 'suspicious';
}

interface SecurityEventAPI {
  id?: string;
  _id?: string;
  eventType: string;
  timestamp: string;
  severity: 'info' | 'warning' | 'critical';
  ipAddress?: string;
  metadata: Record<string, unknown>;
}

interface SecurityEvent {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: Date;
  severity: 'low' | 'medium' | 'high';
}

interface PaginationMetadata {
  total?: number;
  pages?: number;
  totalPages?: number;
}

interface LoginHistoryResponseAPI {
  data?: LoginHistoryAPI[];
  entries?: LoginHistoryAPI[];
  total?: number;
  pagination?: PaginationMetadata;
}

interface SecurityEventsResponseAPI {
  data?: SecurityEventAPI[];
  events?: SecurityEventAPI[];
  total?: number;
  pagination?: PaginationMetadata;
}

const ACTIVITY_PAGE_SIZE = 10;

const formatEventType = (eventType: string): string => {
  const titles: Record<string, string> = {
    login_success: 'Successful login',
    login_failed: 'Failed login attempt',
    password_changed: 'Password changed', // pragma: allowlist secret
    two_factor_enabled: '2FA enabled',
    two_factor_disabled: '2FA disabled',
    session_created: 'New session created',
    session_revoked: 'Session revoked',
    account_locked: 'Account locked',
    account_unlocked: 'Account unlocked',
    profile_updated: 'Profile updated',
    avatar_updated: 'Avatar updated',
    email_changed: 'Email changed',
    email_verified: 'Email verified',
  };
  return titles[eventType] || eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getAlertDescription = (eventType: string, metadata: Record<string, unknown>): string => {
  if (metadata.device) {
    return `From ${metadata.device} device`;
  }
  if (metadata.ipAddress) {
    return `From IP: ${metadata.ipAddress}`;
  }
  return formatEventType(eventType);
};

const mapSeverity = (severity: 'info' | 'warning' | 'critical'): 'low' | 'medium' | 'high' => {
  switch (severity) {
    case 'critical': return 'high';
    case 'warning': return 'medium';
    default: return 'low';
  }
};

const buildActivityUrl = (
  path: string,
  page: number,
  extraParams: Record<string, string> = {}
) => {
  const params = new URLSearchParams({
    limit: ACTIVITY_PAGE_SIZE.toString(),
    offset: ((page - 1) * ACTIVITY_PAGE_SIZE).toString(),
    ...extraParams,
  });

  return `${path}?${params.toString()}`;
};

export const SecurityTab: React.FC = () => {
  const { logout } = useAuthStore();
  // ─── Password state ─────────────────────────────
  const [passwordForm, setPasswordForm] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // ─── 2FA state ──────────────────────────────────
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus>({
    enabled: false,
    hasBackupCodes: false,
  });
  const [isLoadingTwoFactor, setIsLoadingTwoFactor] = useState(true);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [isDisablingTwoFactor, setIsDisablingTwoFactor] = useState(false);
  const [isDisableTwoFactorOpen, setIsDisableTwoFactorOpen] = useState(false);
  const [disableTwoFactorForm, setDisableTwoFactorForm] = useState({
    password: '',
    token: '',
  });
  const [isGeneratingBackupCodes, setIsGeneratingBackupCodes] = useState(false);
  const [generatedBackupCodes, setGeneratedBackupCodes] = useState<string[]>([]);
  const [isBackupCodesDialogOpen, setIsBackupCodesDialogOpen] = useState(false);

  // ─── Sessions state ─────────────────────────────
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [terminatingSessionId, setTerminatingSessionId] = useState<string | null>(null);
  const [isSigningOutAll, setIsSigningOutAll] = useState(false);

  // ─── Activity state (from former ActivityTab) ───
  const [isSecurityAlertsOpen, setIsSecurityAlertsOpen] = useState(true);
  const [loginHistoryPage, setLoginHistoryPage] = useState(1);
  const [loginHistoryFilter, setLoginHistoryFilter] = useState<
    'all' | 'success' | 'failed' | 'suspicious'
  >('all');
  const [securityEventsPage, setSecurityEventsPage] = useState(1);

  const [securityAlerts, setSecurityAlerts] = useState<SecurityAlert[]>([]);
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([]);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [loginHistoryTotal, setLoginHistoryTotal] = useState(0);
  const [securityEventsTotal, setSecurityEventsTotal] = useState(0);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);

  // ─── Fetch effects ─────────────────────────────

  const fetch2FAStatus = useCallback(async () => {
    try {
      const response = await api.get<TwoFactorStatus>('/2fa/status');
      if (response.data) {
        setTwoFactorStatus({
          enabled: response.data.enabled,
          hasBackupCodes: response.data.hasBackupCodes,
          backupCodesGeneratedAt: response.data.backupCodesGeneratedAt,
        });
      }
    } catch {
      // User may not have 2FA set up yet
      setTwoFactorStatus({ enabled: false, hasBackupCodes: false });
    } finally {
      setIsLoadingTwoFactor(false);
    }
  }, []);

  // Fetch 2FA status on mount
  useEffect(() => {
    fetch2FAStatus();
  }, [fetch2FAStatus]);

  // Fetch active sessions on mount
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await api.get<{ sessions: Session[] }>('/sessions');
        if (response.data?.sessions) {
          setSessions(response.data.sessions);
        }
      } catch {
        setSessions([]);
      } finally {
        setIsLoadingSessions(false);
      }
    };

    fetchSessions();
  }, []);

  // Fetch security alerts
  useEffect(() => {
    const fetchSecurityAlerts = async () => {
      try {
        const response = await api.get<SecurityAlertAPI[]>('/users/me/security-alerts');
        if (response.data && Array.isArray(response.data)) {
          const alerts: SecurityAlert[] = response.data.map((alert) => ({
            id: alert.id ?? alert._id ?? `${alert.timestamp}-${alert.eventType}-${alert.severity}`,
            type: alert.severity === 'critical' ? 'critical' : alert.severity === 'warning' ? 'warning' : 'info',
            title: formatEventType(alert.eventType),
            description: getAlertDescription(alert.eventType, alert.metadata),
            timestamp: new Date(alert.timestamp),
          }));
          setSecurityAlerts(alerts);
        }
      } catch (error: unknown) {
        console.debug('Security alerts not available:', errorUtils.getErrorMessage(error));
      } finally {
        setIsLoadingAlerts(false);
      }
    };

    fetchSecurityAlerts();
  }, []);

  // Fetch login history
  useEffect(() => {
    let isCurrent = true;

    const fetchLoginHistory = async () => {
      setIsLoadingHistory(true);

      try {
        const statusParam =
          loginHistoryFilter === 'all'
            ? undefined
            : loginHistoryFilter === 'suspicious'
              ? 'blocked'
              : loginHistoryFilter;

        const response = await api.get<LoginHistoryResponseAPI>(
          buildActivityUrl(
            '/users/me/login-history',
            loginHistoryPage,
            statusParam ? { status: statusParam } : {}
          )
        );
        const apiData = response.data;
        const loginEntries = apiData?.data ?? apiData?.entries ?? [];

        if (Array.isArray(loginEntries)) {
          const history: LoginHistory[] = loginEntries.map((item) => ({
            id: item.id ?? item._id ?? `${item.timestamp}-${item.ipAddress}-${item.method}`,
            date: new Date(item.timestamp),
            device: `${item.device.browser} on ${item.device.os}`,
            location: 'Unknown',
            ipAddress: item.ipAddress,
            status: item.status === 'blocked' ? 'suspicious' : item.status,
          }));

          if (!isCurrent) return;
          setLoginHistory(history);
          setLoginHistoryTotal(
            apiData?.total ??
              apiData?.pagination?.total ??
              response.pagination?.total ??
              history.length
          );
        }
      } catch (error: unknown) {
        console.debug('Login history not available:', errorUtils.getErrorMessage(error));
        if (isCurrent) {
          setLoginHistory([]);
          setLoginHistoryTotal(0);
        }
      } finally {
        if (isCurrent) {
          setIsLoadingHistory(false);
        }
      }
    };

    fetchLoginHistory();

    return () => {
      isCurrent = false;
    };
  }, [loginHistoryFilter, loginHistoryPage]);

  // Fetch security events
  useEffect(() => {
    let isCurrent = true;

    const fetchSecurityEvents = async () => {
      setIsLoadingEvents(true);

      try {
        const response = await api.get<SecurityEventsResponseAPI>(
          buildActivityUrl('/users/me/security-events', securityEventsPage)
        );
        const apiData = response.data;
        const eventEntries = apiData?.data ?? apiData?.events ?? [];

        if (Array.isArray(eventEntries)) {
          const events: SecurityEvent[] = eventEntries.map((event) => ({
            id: event.id ?? event._id ?? `${event.timestamp}-${event.eventType}-${event.ipAddress ?? 'unknown'}`,
            type: event.eventType.includes('login') ? 'authentication' : 'security',
            title: formatEventType(event.eventType),
            description: event.ipAddress ? `From IP: ${event.ipAddress}` : formatEventType(event.eventType),
            timestamp: new Date(event.timestamp),
            severity: mapSeverity(event.severity),
          }));

          if (!isCurrent) return;
          setSecurityEvents(events);
          setSecurityEventsTotal(
            apiData?.total ??
              apiData?.pagination?.total ??
              response.pagination?.total ??
              events.length
          );
        }
      } catch (error: unknown) {
        console.debug('Security events not available:', errorUtils.getErrorMessage(error));
        if (isCurrent) {
          setSecurityEvents([]);
          setSecurityEventsTotal(0);
        }
      } finally {
        if (isCurrent) {
          setIsLoadingEvents(false);
        }
      }
    };

    fetchSecurityEvents();

    return () => {
      isCurrent = false;
    };
  }, [securityEventsPage]);

  // ─── Password handlers ─────────────────────────

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const calculatePasswordStrength = (password: string): PasswordStrength => {
    if (!password) return { score: 0, label: '', color: '' };

    let score = 0;
    if (password.length >= 8) score += 25;
    if (password.length >= 12) score += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 25;
    if (/\d/.test(password)) score += 12.5;
    if (/[^a-zA-Z0-9]/.test(password)) score += 12.5;

    let label = '';
    let color = '';
    if (score < 40) {
      label = 'Weak';
      color = 'bg-destructive';
    } else if (score < 60) {
      label = 'Fair';
      color = 'bg-amber-500';
    } else if (score < 80) {
      label = 'Good';
      color = 'bg-emerald-500';
    } else {
      label = 'Strong';
      color = 'bg-primary';
    }

    return { score, label, color };
  };

  const passwordStrength = calculatePasswordStrength(passwordForm.newPassword);

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await api.put('/users/me/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      toast.success(response.message || 'Password changed successfully. Please log in again.');
      await logout();
    } catch (error: unknown) {
      const message = errorUtils.getErrorMessageOrFallback(error, 'Failed to change password. Please try again.');
      toast.error(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  // ─── 2FA handlers ──────────────────────────────

  const handleTwoFactorEnabled = () => {
    setTwoFactorStatus({
      enabled: true,
      hasBackupCodes: true,
      backupCodesGeneratedAt: new Date().toISOString(),
    });
    setShowTwoFactorSetup(false);
    toast.success('Two-factor authentication enabled successfully');
  };

  const canDisableTwoFactor =
    disableTwoFactorForm.password.trim().length > 0 &&
    /^\d{6}$/.test(disableTwoFactorForm.token);

  const handleDisableTwoFactor = async () => {
    if (!canDisableTwoFactor) {
      toast.error('Enter your password and a valid 6-digit authentication code');
      return;
    }

    setIsDisablingTwoFactor(true);
    try {
      await api.post('/2fa/disable', {
        password: disableTwoFactorForm.password,
        token: disableTwoFactorForm.token,
      });
      setTwoFactorStatus({ enabled: false, hasBackupCodes: false });
      setDisableTwoFactorForm({ password: '', token: '' });
      setIsDisableTwoFactorOpen(false);
      toast.success('Two-factor authentication disabled');
    } catch (error: unknown) {
      const message = errorUtils.getErrorMessageOrFallback(error, 'Failed to disable two-factor authentication');
      toast.error(message);
    } finally {
      setIsDisablingTwoFactor(false);
    }
  };

  const handleGenerateBackupCodes = async () => {
    setIsGeneratingBackupCodes(true);
    try {
      const response = await api.get<{ backupCodes: string[] }>('/2fa/backup-codes');
      if (response.data?.backupCodes) {
        setGeneratedBackupCodes(response.data.backupCodes);
        setIsBackupCodesDialogOpen(true);
        setTwoFactorStatus((prev) => ({
          ...prev,
          hasBackupCodes: true,
          backupCodesGeneratedAt: new Date().toISOString(),
        }));
        toast.success(`${response.data.backupCodes.length} new backup codes generated`);
      }
    } catch {
      toast.error('Failed to generate backup codes');
    } finally {
      setIsGeneratingBackupCodes(false);
    }
  };

  // ─── Session handlers ──────────────────────────

  const handleTerminateSession = async (sessionId: string) => {
    setTerminatingSessionId(sessionId);
    try {
      await api.delete(`/sessions/${sessionId}`);
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      toast.success('Session terminated successfully');
    } catch (error: unknown) {
      const message = errorUtils.getErrorMessageOrFallback(error, 'Failed to terminate session');
      toast.error(message);
    } finally {
      setTerminatingSessionId(null);
    }
  };

  const handleSignOutAllDevices = async () => {
    setIsSigningOutAll(true);
    try {
      await api.delete('/sessions');
      const response = await api.get<{ sessions: Session[] }>('/sessions');
      if (response.data?.sessions) {
        setSessions(response.data.sessions);
      }
      toast.success('Signed out from all other devices');
    } catch (error: unknown) {
      const message = errorUtils.getErrorMessageOrFallback(error, 'Failed to sign out from all devices');
      toast.error(message);
    } finally {
      setIsSigningOutAll(false);
    }
  };

  // ─── Display helpers ───────────────────────────

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="h-4 w-4" />;
      case 'tablet':
        return <Tablet className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  const formatLastActive = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  const loginHistoryPageCount = Math.max(
    1,
    Math.ceil(loginHistoryTotal / ACTIVITY_PAGE_SIZE)
  );
  const safeLoginHistoryPage = Math.min(loginHistoryPage, loginHistoryPageCount);
  const loginHistoryStartIndex = (safeLoginHistoryPage - 1) * ACTIVITY_PAGE_SIZE;
  const paginatedLoginHistory = loginHistory;
  const loginHistoryShowingCount = Math.min(
    loginHistoryStartIndex + paginatedLoginHistory.length,
    loginHistoryTotal
  );

  const securityEventsPageCount = Math.max(
    1,
    Math.ceil(securityEventsTotal / ACTIVITY_PAGE_SIZE)
  );
  const safeSecurityEventsPage = Math.min(securityEventsPage, securityEventsPageCount);
  const securityEventsStartIndex = (safeSecurityEventsPage - 1) * ACTIVITY_PAGE_SIZE;
  const paginatedSecurityEvents = securityEvents;
  const securityEventsShowingCount = Math.min(
    securityEventsStartIndex + paginatedSecurityEvents.length,
    securityEventsTotal
  );

  useEffect(() => {
    setLoginHistoryPage(1);
  }, [loginHistoryFilter]);

  useEffect(() => {
    if (loginHistoryPage > loginHistoryPageCount) {
      setLoginHistoryPage(loginHistoryPageCount);
    }
  }, [loginHistoryPage, loginHistoryPageCount]);

  useEffect(() => {
    if (securityEventsPage > securityEventsPageCount) {
      setSecurityEventsPage(securityEventsPageCount);
    }
  }, [securityEventsPage, securityEventsPageCount]);

  const handleLoginHistoryFilterChange = (filter: typeof loginHistoryFilter) => {
    setLoginHistoryFilter(filter);
    setLoginHistoryPage(1);
  };

  const getAlertIcon = (type: SecurityAlert['type']) => {
    switch (type) {
      case 'critical':
        return <Shield className="h-5 w-5 text-destructive" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-600" />;
    }
  };

  const getStatusBadge = (status: LoginHistory['status']) => {
    const variants = {
      success: 'default' as const,
      failed: 'destructive' as const,
      suspicious: 'secondary' as const,
    };

    return (
      <Badge variant={variants[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getSeverityBadge = (severity: SecurityEvent['severity']) => {
    const colors = {
      low: 'bg-primary text-white',
      medium: 'bg-amber-500 text-white',
      high: 'bg-destructive text-white',
    };

    return (
      <Badge className={colors[severity]}>
        {severity.charAt(0).toUpperCase() + severity.slice(1)}
      </Badge>
    );
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ─── Render ────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Change Password Section */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>
            Update your password to keep your account secure
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">
              Current Password <span className="text-destructive">*</span>
            </Label>
            <Input
              id="currentPassword"
              name="currentPassword"
              type="password"
              value={passwordForm.currentPassword}
              onChange={handlePasswordInputChange}
              placeholder="Enter your current password"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">
              New Password <span className="text-destructive">*</span>
            </Label>
            <Input
              id="newPassword"
              name="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordInputChange}
              placeholder="Enter your new password"
              required
            />
            {passwordForm.newPassword && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Password strength:
                  </span>
                  <span className="text-xs font-medium">{passwordStrength.label}</span>
                </div>
                <Progress value={passwordStrength.score} className="h-2" />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Use at least 8 characters with a mix of uppercase, lowercase, numbers,
              and symbols.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">
              Confirm New Password <span className="text-destructive">*</span>
            </Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordInputChange}
              placeholder="Confirm your new password"
              required
            />
            {passwordForm.confirmPassword &&
              passwordForm.newPassword !== passwordForm.confirmPassword && (
                <p className="text-xs text-destructive">Passwords do not match</p>
              )}
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleChangePassword}
              disabled={
                isChangingPassword ||
                !passwordForm.currentPassword ||
                !passwordForm.newPassword ||
                !passwordForm.confirmPassword ||
                passwordForm.newPassword !== passwordForm.confirmPassword
              }
            >
              {isChangingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isChangingPassword ? 'Changing Password...' : 'Change Password'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication Section */}
      <Card>
        <CardHeader>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingTwoFactor ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Status</p>
                  <p className="text-xs text-muted-foreground">
                    Two-factor authentication is{' '}
                    {twoFactorStatus.enabled ? 'enabled' : 'disabled'}
                  </p>
                </div>
                <Badge variant={twoFactorStatus.enabled ? 'default' : 'secondary'}>
                  {twoFactorStatus.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {twoFactorStatus.enabled ? 'Disable' : 'Enable'} Two-Factor Authentication
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {twoFactorStatus.enabled
                      ? 'Remove the extra security layer from your account'
                      : 'Use an authenticator app to generate verification codes'}
                  </p>
                </div>
                {twoFactorStatus.enabled ? (
                  <AlertDialog
                    open={isDisableTwoFactorOpen}
                    onOpenChange={(open) => {
                      setIsDisableTwoFactorOpen(open);
                      if (!open) {
                        setDisableTwoFactorForm({ password: '', token: '' });
                      }
                    }}
                  >
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={isDisablingTwoFactor}>
                        {isDisablingTwoFactor && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {isDisablingTwoFactor ? 'Disabling...' : 'Disable'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Disable Two-Factor Authentication?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove the extra security layer from your account.
                          Enter your password and a current authenticator code to continue.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="disable-2fa-password">Password</Label>
                          <Input
                            id="disable-2fa-password"
                            type="password"
                            value={disableTwoFactorForm.password}
                            onChange={(event) =>
                              setDisableTwoFactorForm((prev) => ({
                                ...prev,
                                password: event.target.value,
                              }))
                            }
                            placeholder="Enter your password"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="disable-2fa-token">Authentication code</Label>
                          <Input
                            id="disable-2fa-token"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            value={disableTwoFactorForm.token}
                            onChange={(event) =>
                              setDisableTwoFactorForm((prev) => ({
                                ...prev,
                                token: event.target.value.replace(/\D/g, '').slice(0, 6),
                              }))
                            }
                            placeholder="000000"
                          />
                        </div>
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <Button
                          onClick={handleDisableTwoFactor}
                          disabled={!canDisableTwoFactor || isDisablingTwoFactor}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {isDisablingTwoFactor && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Disable 2FA
                        </Button>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : (
                  <Button onClick={() => setShowTwoFactorSetup(true)}>
                    Enable
                  </Button>
                )}
              </div>

              {twoFactorStatus.enabled && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Backup Codes</p>
                      <p className="text-xs text-muted-foreground">
                        {twoFactorStatus.hasBackupCodes
                          ? 'Backup codes have been generated'
                          : 'No backup codes have been generated'}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleGenerateBackupCodes}
                      disabled={isGeneratingBackupCodes}
                    >
                      {isGeneratingBackupCodes && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Generate New Codes
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Active Sessions Section */}
      <Card>
        <CardHeader>
          <CardTitle>Active Sessions</CardTitle>
          <CardDescription>
            Manage your active sessions and sign out from other devices
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingSessions ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              No active sessions found
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map((session, index) => (
                <div
                  key={session.sessionId}
                  className="flex items-start justify-between rounded-lg border p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-muted-foreground">
                      {getDeviceIcon(session.device?.type)}
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">
                        {session.device?.browser || 'Unknown Browser'} on {session.device?.os || 'Unknown OS'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {session.ipAddress || 'Unknown IP'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Last active: {formatLastActive(session.lastActivityAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {index === 0 ? (
                      <Badge variant="outline">Current</Badge>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={terminatingSessionId === session.sessionId}
                          >
                            {terminatingSessionId === session.sessionId ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>End this session?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This device will be signed out immediately. You&apos;ll need to sign in again to use this device.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleTerminateSession(session.sessionId)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Sign Out
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {sessions.length > 1 && (
            <>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Sign Out All Other Devices</p>
                  <p className="text-xs text-muted-foreground">
                    This will sign you out from all devices except this one
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={isSigningOutAll}>
                      {isSigningOutAll && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {isSigningOutAll ? 'Signing Out...' : 'Sign Out All'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Sign out of all devices?</AlertDialogTitle>
                      <AlertDialogDescription>
                        You will be signed out from all devices except this one. You&apos;ll need to sign in again on all your other devices.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleSignOutAllDevices}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Sign Out All Devices
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Recent Security Alerts */}
      <Card>
        <Collapsible open={isSecurityAlertsOpen} onOpenChange={setIsSecurityAlertsOpen}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Security Alerts</CardTitle>
                <CardDescription>
                  Critical and warning events from the last 7 days
                </CardDescription>
              </div>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  {isSecurityAlertsOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              {isLoadingAlerts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : securityAlerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Shield className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-sm font-medium text-foreground mb-1">
                    No security alerts
                  </p>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    You haven&apos;t received any security alerts in the last 7 days. We&apos;ll notify you here if we detect any suspicious activity on your account.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {securityAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="flex items-start gap-4 rounded-lg border p-4"
                    >
                      {getAlertIcon(alert.type)}
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">{alert.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {alert.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(alert.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Login History */}
      <Card>
        <CardHeader>
          <CardTitle>Login History</CardTitle>
          <CardDescription>
            Recent login attempts and active sessions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={loginHistoryFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleLoginHistoryFilterChange('all')}
            >
              All
            </Button>
            <Button
              variant={loginHistoryFilter === 'success' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleLoginHistoryFilterChange('success')}
            >
              Success
            </Button>
            <Button
              variant={loginHistoryFilter === 'failed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleLoginHistoryFilterChange('failed')}
            >
              Failed
            </Button>
            <Button
              variant={loginHistoryFilter === 'suspicious' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleLoginHistoryFilterChange('suspicious')}
            >
              Suspicious
            </Button>
          </div>

          <Separator />

          {/* Login History Table */}
          {isLoadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : loginHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Clock className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-sm font-medium text-foreground mb-1">
                No login history found
              </p>
              <p className="text-xs text-muted-foreground max-w-sm">
                {loginHistoryFilter === 'all'
                  ? "Your login activity will appear here. This includes successful logins, failed attempts, and suspicious activity."
                  : `No ${loginHistoryFilter} login attempts found. Try selecting a different filter.`
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedLoginHistory.map((login) => (
                  <TableRow key={login.id}>
                    <TableCell className="font-medium">
                      {formatDateTime(login.date)}
                    </TableCell>
                    <TableCell>{login.device}</TableCell>
                    <TableCell>{login.location}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {login.ipAddress}
                    </TableCell>
                    <TableCell>{getStatusBadge(login.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {loginHistoryTotal > ACTIVITY_PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {loginHistoryShowingCount} of{' '}
                {loginHistoryTotal} entries
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLoginHistoryPage((p) => Math.max(1, p - 1))}
                  disabled={safeLoginHistoryPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLoginHistoryPage((p) => Math.min(loginHistoryPageCount, p + 1))}
                  disabled={safeLoginHistoryPage >= loginHistoryPageCount}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Events Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Security Events</CardTitle>
          <CardDescription>
            Complete timeline of security-related activities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingEvents ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : securityEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Activity className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-sm font-medium text-foreground mb-1">
                No security events yet
              </p>
              <p className="text-xs text-muted-foreground max-w-sm">
                Your complete security timeline will appear here, including password changes, login attempts, 2FA events, and other security-related activities.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {paginatedSecurityEvents.map((event, index) => (
                  <div key={event.id} className="flex gap-4">
                    <div className="relative flex flex-col items-center">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 bg-background">
                        <Shield className="h-4 w-4" />
                      </div>
                      {index < paginatedSecurityEvents.length - 1 && (
                        <div className="h-full w-px bg-border" />
                      )}
                    </div>
                    <div className="flex-1 pb-8">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{event.title}</p>
                            {getSeverityBadge(event.severity)}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {event.description}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(event.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination for Security Events */}
              {securityEventsTotal > ACTIVITY_PAGE_SIZE && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Showing {securityEventsShowingCount}{' '}
                      of {securityEventsTotal} events
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSecurityEventsPage((p) => Math.max(1, p - 1))}
                        disabled={safeSecurityEventsPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSecurityEventsPage((p) => Math.min(securityEventsPageCount, p + 1))}
                        disabled={safeSecurityEventsPage >= securityEventsPageCount}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Two-Factor Setup Modal */}
      <TwoFactorSetup
        isOpen={showTwoFactorSetup}
        onClose={() => setShowTwoFactorSetup(false)}
        onEnabled={fetch2FAStatus}
        onComplete={handleTwoFactorEnabled}
      />

      <AlertDialog
        open={isBackupCodesDialogOpen}
        onOpenChange={(open) => {
          setIsBackupCodesDialogOpen(open);
          if (!open) {
            setGeneratedBackupCodes([]);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>New Backup Codes</AlertDialogTitle>
            <AlertDialogDescription>
              Store these codes securely. Each code can only be used once.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {generatedBackupCodes.map((code) => (
              <div key={code} className="rounded border bg-muted/50 p-2 text-center font-mono text-sm">
                {code}
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogAction>Done</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
